import { invoke } from "@tauri-apps/api/core";
import * as fs from "@tauri-apps/plugin-fs";
import YAML from "yaml";

import { Logger } from "@moment/logging";

import { env } from "../env";

const logger = new Logger("fs");
export const appEnv = env.appEnv ?? "local";

/* -------------------------------
 *  File Operations
 *
 *  - We explicitly do not set write/mkdir modes and let the system handle those
 * ------------------------------- */

/* -------------------------------
 *  Notes about file operations in mobile
 *
 *  For iOS, Tauri recommends using the $DOCUMENT directory, not the $HOME directory, for local file writes.
 *  As that is counted as User space.
 *
 * 	As a temporary workaround, we have to use fsios (as that code uses Swift URLs rather than path names)
 * 	which requires us to use relative paths, and not full paths.
 *  Once Tauri fixes this bug, we can remove the custom code.
 * ------------------------------- */

function getBaseDir() {
	if (env.isMobileApp) {
		return fs.BaseDirectory.Document;
	}
	return fs.BaseDirectory.Home;
}

export async function writeTextFile(filePath: string, content: string) {
	if (!env.isDesktopApp && !env.isMobileApp) {
		logger.error("fs.writeTextFile called in non-desktop mode", {
			filePath,
			content,
		});
		return;
	}

	// if (isMobileAppMode()) {
	// 	const relativePath = await getRelativePath(filePath);
	// 	logger.debug("Writing to path " + relativePath);

	// 	return iosfs.writeFile(relativePath, content);
	// }

	// Part of the Temporary Workaround ////////////////////////////////////////
	const encoder = new TextEncoder();
	return await invoke("write_text_file", encoder.encode(content), {
		headers: {
			path: encodeURIComponent(filePath),
			options: JSON.stringify({}),
		},
	});
	// End of the Temporary Workaround /////////////////////////////////////////

	// Pre-temporary workaround code ///////////////////////////////////////////
	// return fs.writeTextFile(filePath, content, options);
}

export async function writeJsonFile(filePath: string, content: object) {
	return writeTextFile(filePath, JSON.stringify(content, null, "\t") + "\n");
}

export async function writeYamlFile(filePath: string, content: object) {
	return writeTextFile(filePath, YAML.stringify(content));
}

export async function writeBinaryFile(filePath: string, content: Uint8Array<ArrayBufferLike>) {
	if (!env.isDesktopApp && !env.isMobileApp) {
		logger.error("fs.writeBinaryFile called in non-desktop mode", {
			filePath,
			content,
		});
		return;
	}

	// if (isMobileAppMode()) {
	// 	logger.error("writing images or binaries in mobile is not supported yet");
	// 	throw new Error("Not implemented");
	// }

	return fs.writeFile(filePath, content);
}

export async function readTextFile(filePath: string) {
	if (!env.isDesktopApp && !env.isMobileApp) {
		logger.error("fs.readTextFile called in non-desktop mode", {
			filePath,
		});
		return "";
	}

	return fs.readTextFile(filePath, {
		baseDir: getBaseDir(),
	});
}

export async function readJsonFile<T>(filePath: string): Promise<T | undefined> {
	try {
		const text = await readTextFile(filePath);
		return JSON.parse(text) as T;
	} catch (error) {
		logger.error("Failed to read JSON file " + filePath + ": " + JSON.stringify(error));
		return undefined;
	}
}

export async function readYamlFile<T>(filePath: string): Promise<T | undefined> {
	try {
		const text = await readTextFile(filePath);
		return YAML.parse(text) as T;
	} catch (error) {
		logger.error("Failed to read YAML file", { filePath, error });
		return undefined;
	}
}

export async function readBinaryFile(filePath: string) {
	if (!env.isDesktopApp && !env.isMobileApp) {
		logger.error("fs.readBinaryFile called in non-desktop mode", {
			filePath,
		});
		return undefined;
	}

	try {
		return fs.readFile(filePath, {
			baseDir: getBaseDir(),
		});
	} catch (error) {
		logger.error("Failed to read binary file", { filePath, error });
		return undefined;
	}
}

export async function exists(filePath: string) {
	if (!env.isDesktopApp && !env.isMobileApp) {
		logger.error("fs.exists called in non-desktop mode", {
			filePath,
		});
		return false;
	}

	try {
		return fs.exists(filePath, {
			baseDir: getBaseDir(),
		});
	} catch (error) {
		logger.error("Failed to check if file exists", { filePath, error });
		return false;
	}
}

export async function create(filePath: string) {
	if (!env.isDesktopApp && !env.isMobileApp) {
		logger.error("fs.create called in non-desktop mode", {
			filePath,
		});
		return;
	}

	try {
		return fs.create(filePath, { baseDir: getBaseDir() });
	} catch (error) {
		logger.error("Failed to create file", { filePath, error });
		throw error;
	}
}

export async function mkdir(dirPath: string) {
	if (!env.isDesktopApp && !env.isMobileApp) {
		logger.error("fs.mkdir called in non-desktop mode", {
			dirPath,
		});
		return;
	}

	try {
		return fs.mkdir(dirPath, {
			recursive: true,
			baseDir: getBaseDir(),
		});
	} catch (error) {
		logger.error("Failed to create directory " + dirPath + ": " + JSON.stringify(error));
		throw error;
	}
}

export async function remove(filePath: string) {
	if (!env.isDesktopApp && !env.isMobileApp) {
		logger.error("fs.remove called in non-desktop mode", {
			filePath,
		});
		return;
	}

	try {
		return fs.remove(filePath, {
			recursive: true,
			baseDir: getBaseDir(),
		});
	} catch (error) {
		logger.error("Failed to remove file", { filePath, error });
		throw error;
	}
}

export async function rename(oldPath: string, newPath: string) {
	if (!env.isDesktopApp && !env.isMobileApp) {
		logger.error("fs.rename called in non-desktop mode", {
			oldPath,
			newPath,
		});
		return;
	}

	try {
		const baseDir = getBaseDir();
		return fs.rename(oldPath, newPath, {
			oldPathBaseDir: baseDir,
			newPathBaseDir: baseDir,
		});
	} catch (error) {
		logger.error("Failed to rename", { oldPath, newPath, error });
		throw error;
	}
}

export async function readDir(dirPath: string) {
	if (!env.isDesktopApp && !env.isMobileApp) {
		logger.error("fs.readDir called in non-desktop mode", {
			dirPath,
		});
		return [];
	}

	try {
		return fs.readDir(dirPath, { baseDir: getBaseDir() });
	} catch (error) {
		logger.error("Failed to read directory", { dirPath, error });
		throw error;
	}
}
